import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "./motion";
import { IMAGES } from "./data";
import { scrollTo } from "./Header";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      {/* Prism aura background */}
      <motion.div
        style={{ y: yBg, scale: scaleBg }}
        className="prism-aura absolute inset-0 -z-10"
      />
      <motion.img
        style={{ y: yBg }}
        src={IMAGES.heroPrism}
        alt=""
        aria-hidden
        className="absolute inset-0 -z-10 h-full w-full object-cover opacity-40 mix-blend-multiply"
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-transparent to-prism-cream" />

      <motion.div
        style={{ y: yContent, opacity }}
        className="mx-auto max-w-7xl w-full px-6 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-xs font-semibold uppercase tracking-[0.28em] text-text-secondary mb-8"
          style={{ color: "hsl(var(--muted-foreground))" }}
          data-testid="hero-overline"
        >
          Chartered Clinical Psychology · South Dublin
        </motion.p>

        <h1 className="font-serif font-light tracking-tight leading-[0.95] text-primary text-5xl sm:text-7xl lg:text-8xl max-w-5xl">
          <MaskLine delay={0.35}>A gentle, clear space</MaskLine>
          <MaskLine delay={0.5} className="italic prism-text">
            to understand yourself
          </MaskLine>
          <MaskLine delay={0.65}>&amp; those you love.</MaskLine>
        </h1>

        <div className="mt-10 h-[3px] w-40 rounded-full prism-line" />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-10 max-w-xl text-lg font-light leading-relaxed text-text-secondary"
          style={{ color: "hsl(var(--muted-foreground))" }}
          data-testid="hero-subtitle"
        >
          Prism Psychology is the practice of Dr. Bairbre Fee — evidence-based
          therapy, assessment and parent support that meets you with warmth,
          patience and genuine understanding.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-cta"
            onClick={() => scrollTo("booking")}
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
          >
            Book a session
            <ArrowDownRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
            />
          </button>
          <button
            data-testid="hero-services-cta"
            onClick={() => scrollTo("services")}
            className="rounded-full border border-primary/30 px-8 py-4 text-sm text-primary hover:border-primary transition-colors duration-300"
          >
            Explore services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span
          className="text-[10px] uppercase tracking-[0.3em]"
          style={{ color: "hsl(var(--muted-foreground))" }}
        >
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="h-8 w-px bg-primary/40"
        />
      </motion.div>
    </section>
  );
};
